import Casino, { CasinoFavourites, GamesBrand, LEGACY_CASINO } from "../../../../services/casino-feed";
import useEmblaCarousel from "embla-carousel-react"
import CasinoService, { GameEntry } from "../../../../services/casino-feed";
import { useEffect, useState } from "preact/hooks";
import { CasinoPagesInfo, CmsCasinoPagesFetcher } from "./CasinoPagesInfo";


export interface CasinoRetriever {
    gamesForWidget(page: CategoryWidget | ProviderWidget | FavouritesWidget | RecentlyPlayedWidget | ProviderCategoryWidget): GameEntry[];
}

export class NextCasinoRetriever implements CasinoRetriever {
    private map?: Map<string, GameEntry>;
    constructor(
        private readonly providers: GamesBrand[],
    ) {
        this.map = new Map(providers.flatMap(p => p.sub).map((g) => [g.provider+'/'+g.category+'/'+g.id, g]));
    }

    public gamesForWidget(page: CategoryWidget | ProviderWidget | FavouritesWidget | RecentlyPlayedWidget | ProviderCategoryWidget) {
        if (page.type == 'category')
            return this.getGamesForCategory(page);
        if (page.type == 'provider')
            return this.getGamesForProvider(page);
        if (page.type == 'providercategory')
            return []; // Deprecated
        if (page.type == 'favourites')
            return CasinoFavourites.getFavourites();
        if (page.type == 'recently-played')
            return CasinoFavourites.getPlayed();
    }

    public getGamesForProvider(page: ProviderWidget): GameEntry[] {
        const mainGames = page.data.games!.map(g => this.map.get(g.provider+'/'+g.category+'/'+g.id)).filter(x => !!x);
        const priorityIds = new Set([...mainGames.map(g => g.provider+'/'+g.category+'/'+g.id)]);
        const otherGames = this.providers
            .filter((provider) => provider.provider+'/'+provider.id == page.data.provider || provider.id == page.data.provider)
            .flatMap(provider => provider.sub)
            .filter(g => !priorityIds.has(g.provider+'/'+g.category+'/'+g.id));
        return mainGames.concat(otherGames);
    }

    public getGamesForCategory(page: CategoryWidget): GameEntry[] {
        const mainGames = page.data.games!.map(g => this.map.get(g.provider+'/'+g.category+'/'+g.id)).filter(x => !!x);
        return mainGames;
    }
}

export class LegacyCasinoRetriever implements CasinoRetriever {
    constructor(
        private readonly allGamesFlat: GameEntry[]
    ) {
    }

    public gamesForWidget(page: CategoryWidget | ProviderWidget | FavouritesWidget | RecentlyPlayedWidget | ProviderCategoryWidget) {
        if (page.type == 'category')
            return this.getGamesForCategory(page.data.category);
        if (page.type == 'provider')
            return this.getGamesForProvider(page.data.provider);
        if (page.type == 'providercategory')
            return this.getGamesForProvider(page.data.category);
        if (page.type == 'favourites')
            return CasinoFavourites.getFavourites();
        if (page.type == 'recently-played')
            return CasinoFavourites.getPlayed();
    }

    public getGamesForProvider(providerId: string): GameEntry[] {
        if (!this.allGamesFlat || !this.allGamesFlat.length)
            return [];
        const sortedCatSearchKey = providerId.toLowerCase();
        return this.allGamesFlat.filter(game =>
            game.tags.includes(providerId) ||
            game.tags.includes(providerId) ||
            game.tags.includes(providerId.toLowerCase()) ||
            game.tags.filter(t => t.toLowerCase().indexOf(sortedCatSearchKey) == 0).length > 0
        );
    }

    public getGamesForCategory(categoryId: string): GameEntry[] {
        if (!this.allGamesFlat || !this.allGamesFlat.length)
            return [];
        const categoryName = categoryId.startsWith('c:') ? categoryId.substring(2).replace(/\+/g, ' ') : categoryId;
        const isCustomCategory = categoryId.indexOf('c:') === 0;

        const sortedCatSearchKey = (isCustomCategory ? categoryId + ':' : categoryId).toLowerCase();

        const slots = this.allGamesFlat.filter(game =>
            game.tags.includes(categoryId) ||
            game.tags.includes(categoryName) ||
            game.tags.includes(categoryId.toLowerCase()) ||
            game.tags.filter(t => t.toLowerCase().indexOf(sortedCatSearchKey) == 0).length > 0
        );

        if (isCustomCategory) {
            const games = [...slots];
            games.sort((a, b) => {
                let aOrdering = 0,
                    bOrdering = 0;

                const aTags = a.tags.filter(t => t.indexOf(categoryId + ':') !== -1),
                    bTags = b.tags.filter(t => t.indexOf(categoryId + ':') !== -1);

                if (aTags.length) {
                    const tagValue = aTags[aTags.length - 1].split(':').pop();
                    if (tagValue && !isNaN(parseInt(tagValue))) {
                        aOrdering = parseInt(tagValue);
                    }
                }

                if (bTags.length) {
                    const tagValue = bTags[bTags.length - 1].split(':').pop();
                    if (tagValue && !isNaN(parseInt(tagValue))) {
                        bOrdering = parseInt(tagValue);
                    }
                }

                return aOrdering - bOrdering;
            });
            return games;
        }

        return slots;
    }
}

export function useCasinoSlots() {
    const [slots, setSlots] = useState(() => Casino.flattenSlotList());

    useEffect(() => {
        const cb = () => setSlots(Casino.flattenSlotList());
        Casino.listenChanges(cb);
        return () => Casino.unlistenChanges(cb)
    }, []);
    useEffect(() => {
        setSlots(Casino.flattenSlotList());
    }, [Casino.providersList()])

    return slots;
}

export function useCasinoProviders() {
    const [providers, setProviders] = useState(() => Object.values(Casino.providersList()));

    useEffect(() => {
        const cb = () => setProviders(Object.values(Casino.providersList()));
        Casino.listenChanges(cb);
        return () => Casino.unlistenChanges(cb)
    }, []);
    useEffect(() => {
        setProviders(Object.values(Casino.providersList()));
    }, [Casino.providersList()])

    return providers;
}

export function useCasinoPageInfo() {
    const [pagesInfo, setPagesInfo] = useState<CasinoPagesInfo>(() => {
        if (CasinoService.INSTANCE === LEGACY_CASINO)
            return new CasinoPagesInfo();
        const preBuilt = new CmsCasinoPagesFetcher(CasinoService.asNewCasino()).preBuild();
        if (preBuilt)
            return preBuilt
    });
    useEffect(() => {
        if (pagesInfo != null)
            return;
        new CmsCasinoPagesFetcher(CasinoService.asNewCasino()).build().then(setPagesInfo);
    }, [])
    return pagesInfo;
}

/**
 * Divide la lista giochi in una serie di pagine (griglie) rowsXcols da mettere nel carosello.
 * 
 * @param cols 
 * @param rows 
 * @param gamesFinder 
 * @param page 
 * @returns 
 */
export function useGamesRow(cols: number, rows: number, gamesFinder: CasinoRetriever, page: CasinoWidget) {
    const games = useFilteredGamesList(page, gamesFinder);
    const pageSize = cols * rows;
    const numPages = 6;
    const chunkedGames = Array.from({ length: numPages }, (_, i) => games.slice(i * pageSize, i * pageSize + pageSize)).filter(r => r.length > 0);

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

    const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
    const scrollNext = () => emblaApi && emblaApi.scrollNext();

    return { scrollPrev, scrollNext, emblaRef, chunkedGames };
}

/**
 * Usato per:
 * - generare la pagina con infinite scroll
 * - generare la preview
 * @param page 
 * @param gamesFinder 
 * @returns 
 */
export function useFilteredGamesList(page: CasinoWidget, gamesFinder: CasinoRetriever): GameEntry[] {
    const [games, setGames] = useState(() => gamesFinder.gamesForWidget(page));
    useEffect(() => setGames(gamesFinder.gamesForWidget(page)), [page, gamesFinder])
    return games;
}

export function getGameRowUrl(page: CasinoWidget, mainPage: string) {
    const type = page.type === 'provider' || page.type === 'providercategory' ? 'p' : 'c';
    const id = 'id' in page.data ? page.data.id
        : page.type === 'provider' ? page.data.provider 
        : page.type === 'providercategory' ? page.data.provider 
        : page.type === 'category' ? page.data.category
        : page.type;
    return `/${mainPage}/${type}/${encodeURIComponent(id)}`;
}

export function pageInfoFromUrl(type: 'c' | 'p', pageCode: string, pagesInfo: CasinoPagesInfo) {
    if (type == null || pageCode == null)
        return null;
    const pageById = pagesInfo.getWidgetBySlug(pageCode);
    if (pageById != null) {
        return pageById;
    }
    if (type == 'c') {
        if (pageCode == 'favourites') {
            return { type: "favourites", data: { name: "Favourites" } } as FavouritesWidget;
        }
        if (pageCode == 'recently-played') {
            return { type: "recently-played", data: { name: "Recently Played" } } as RecentlyPlayedWidget;
        }
        return { type: "category", data: { category: pageCode, name: pageCode.substring(2).replace(/\+/, ' ') } } as CategoryWidget;
    }
    const providers: { [k: string]: GamesBrand } = Casino.providersList();
    const thisProvider = Object.values(providers).find(p => p.id.toLowerCase() == pageCode) || { name: pageCode }
    return { type: "provider", data: { provider: pageCode, name: thisProvider.name } } as ProviderWidget;
}

export type CarouselWidget = {
    type: 'carousel',
    data: CasinoSlide[]
}

export type ProviderWidget = {
    type: 'provider',
    data: {
        id?: string,
        provider: string,
        name: string,
        icon?: string,
        cols: number,
        rows: number,
        games?: Record<'id' | 'category' | 'provider', GameEntry>[]
    }
}

export type CategoryWidget = {
    type: 'category',
    data: {
        id?: string,
        category: string,
        name: string,
        icon?: string,
        cols: number,
        rows: number,
        games?: Record<'id' | 'category' | 'provider', GameEntry>[]
    }
}
export type ProviderCategoryWidget = {
    type: 'providercategory',
    data: {
        id?: string,
        category: string,
        provider: string,
        name: string,
        icon?: string,
        cols: number,
        rows: number,
    }
}

export type FavouritesWidget = {
    type: 'favourites',
    data: {
        name: "Favourites",
        icon?: string,
        cols: number,
        rows: number,
    }
}

export type RecentlyPlayedWidget = {
    type: 'recently-played',
    data: {
        name: "Recently Played",
        icon?: string,
        cols: number,
        rows: number,
    }
}

export type CasinoWidget = ProviderWidget | CategoryWidget | FavouritesWidget | RecentlyPlayedWidget | ProviderCategoryWidget;

export interface CasinoSlide {
    img: string,
    title: string,
    subtitle: string,
}

